import React, { useEffect, useMemo, useRef, useState } from "react";
import { Wrapper } from "./style";
import { Helmet } from "react-helmet";
import { Button, ConfigProvider, message, Table, Tag, Pagination, Spin, Modal, Form, Input, Card, Row, Col, Radio, Progress } from "antd";
import {
  CheckCircleFilled,
  ExclamationCircleFilled,
  LeftOutlined,
  RightOutlined,
  DownloadOutlined,
  PlusOutlined,
  CreditCardOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { MakeApiCall } from "../../../apis";
import { BookACallModalUI } from "../../../components/book-a-call/BookACallModal";
import { AddCommas, primaryColor, ENABLE_BILLING_COUPON } from "../../../config";
import { formatToHubSpotDateTime, useHubSpotSync } from "../../../hooks/useHubSpotSync";

// Load Razorpay script
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const Billing = (props) => {
  const { userData } = props;
  const { syncProperties } = useHubSpotSync(userData);
  const hasSyncedBillingVisitRef = useRef(false);

  const [billingPeriod, setBillingPeriod] = useState("yearly");
  const [planCouponInputs, setPlanCouponInputs] = useState({});
  const [appliedPlanCoupons, setAppliedPlanCoupons] = useState({});
  const [validatingPlanCouponId, setValidatingPlanCouponId] = useState(null);
  const plansScrollRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [billingData, setBillingData] = useState(null);

  // Plans fetched dynamically
  const [availablePlans, setAvailablePlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [lastMonthOrderCount, setLastMonthOrderCount] = useState(null);

  // WhatsApp Quota states
  const [whatsappQuota, setWhatsappQuota] = useState(null);
  const [whatsappQuotaLoading, setWhatsappQuotaLoading] = useState(false);

  // Payment History (Invoices) states
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceList, setInvoiceList] = useState([]);
  const [invoicePage, setInvoicePage] = useState(1);
  const [invoicePerPage, setInvoicePerPage] = useState(5);
  const [invoiceTotal, setInvoiceTotal] = useState(0);

  // Payment Methods states
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(false);

  // Billing Info states
  const [billingInfo, setBillingInfo] = useState({
    workspace: "",
    billing_email: "",
    currency: "INR",
    address: "",
    gst_number: ""
  });
  const [billingInfoLoading, setBillingInfoLoading] = useState(false);
  const [showBillingInfoModal, setShowBillingInfoModal] = useState(false);
  const [billingInfoForm] = Form.useForm();

  // Razorpay states
  const [isRazorpayReady, setIsRazorpayReady] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [gatewayOpening, setGatewayOpening] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  // Book-a-call / Calendly modal
  const [bookCallOpen, setBookCallOpen] = useState(false);
  const [bookCallLoading, setBookCallLoading] = useState(true);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [addPaymentForm] = Form.useForm();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Monthly billing confirmation modal (3-option upsell)
  const [monthlyConfirmModal, setMonthlyConfirmModal] = useState({
    open: false,
    plan: null,
    isPlanChange: false,
    actionLabel: '',
    isUpgrade: false,
  });

  // Cancel subscription survey modal
  const [cancelSurveyModal, setCancelSurveyModal] = useState(false);
  const [cancelPrimaryReason, setCancelPrimaryReason] = useState(null);
  const [cancelUnmetExpectations, setCancelUnmetExpectations] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);

  // Fetch billing info
  const getBillingInfo = async () => {
    setBillingInfoLoading(true);
    try {
      const response = await MakeApiCall(
        "billing/info",
        "get",
        null,
        true,
        {},
        userData?.token
      );
      if (response?.status && response?.data) {
        setBillingInfo(response.data);
        // Also set form values when data is available
        billingInfoForm.setFieldsValue(response.data);
      } else {
        // If no billing info exists, set empty/default values
        setBillingInfo({
          workspace: "",
          billing_email: "",
          currency: "INR",
          address: "",
          gst_number: ""
        });
        billingInfoForm.setFieldsValue({
          workspace: "",
          billing_email: "",
          currency: "INR",
          address: "",
          gst_number: ""
        });
      }
    } catch (e) {
      message.error("Error while loading billing info");
      // Set default values on error
      setBillingInfo({
        workspace: "",
        billing_email: "",
        currency: "INR",
        address: "",
        gst_number: ""
      });
    }
    setBillingInfoLoading(false);
  };

  // Fetch WhatsApp Quota
  const getWhatsappQuota = async () => {
    setWhatsappQuotaLoading(true);
    try {
      const response = await MakeApiCall(
        "billing/whatsapp-quota",
        "get",
        null,
        true,
        {},
        userData?.token
      );
      if (response?.status && response?.data) {
        setWhatsappQuota(response.data);
      } else {
        // message.warning(response?.message || "Failed to fetch WhatsApp quota");
      }
    } catch (e) {
      message.error("Error while loading WhatsApp quota");
    }
    setWhatsappQuotaLoading(false);
  };

  // Update billing info
  const saveBillingInfo = async (values) => {
    setBillingInfoLoading(true);
    try {
      // Check if billing info exists (has workspace or billing_email)
      const hasExistingInfo = billingInfo?.workspace || billingInfo?.billing_email;

      // Determine which endpoint to use
      const endpoint = hasExistingInfo ? "billing/update-info" : "billing/create-info";

      const response = await MakeApiCall(
        endpoint,
        "post",
        values,
        true,
        {},
        userData?.token
      );

      if (response?.status) {
        message.success(hasExistingInfo ? "Billing info updated" : "Billing info created");
        setShowBillingInfoModal(false);
        setBillingInfo({
          ...billingInfo,
          ...values
        });
        getBillingInfo(); // Refresh data
      } else {
        message.warning(response?.message || `Failed to ${hasExistingInfo ? "update" : "create"} billing info`);
      }
    } catch (e) {
      message.error("Failed to save billing info");
    }
    setBillingInfoLoading(false);
  };

  useEffect(() => {
    getBillingInfo();
    getWhatsappQuota();
    // eslint-disable-next-line
  }, []);
  
  // Check if Razorpay is loaded
  useEffect(() => {
    const checkRazorpay = async () => {
      const isLoaded = await loadRazorpayScript();
      setIsRazorpayReady(isLoaded);
      if (!isLoaded) {
        message.error("Failed to load payment gateway. Please refresh the page.");
      }
    };
    checkRazorpay();
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Fetch dynamic plans from API
  const getAvailablePlans = async () => {
    setPlansLoading(true);
    try {
      const response = await MakeApiCall(
        `billing/plans`,
        "get",
        null,
        true,
        {},
        userData?.token
      );
      if (response?.status && Array.isArray(response.data?.plans)) {
        const plans = response.data.plans;
        if (response.data.last_30_days_unique_order_count !== undefined) {
          setLastMonthOrderCount(response.data.last_30_days_unique_order_count);
        }
        const formatted = plans.map((plan, idx) => ({
          key: plan.id,
          id: plan.id,
          name: plan.name,
          description: plan.description,
          perks: plan.features,
          priceMonthly: plan.price_monthly,
          priceYearly: plan.price_yearly,
          formattedPriceMonthly: plan.formatted_price_monthly,
          formattedPriceYearly: plan.formatted_price_yearly,
          formattedPriceYearlyAnnual: plan.formatted_price_yearly_annual,
          currency: plan.currency || "INR",
          cta: "Choose " + (plan.name.includes("Plan") ? plan.name.replace(" Plan", "") : plan.name),
          highlighted: idx === 0,
          enabled: plan.enabled !== false,
          maxOrderCount: plan.max_order_count,
          whatsappLimit: plan.whatsapp_limit || 500,
        }));
        setAvailablePlans(formatted);
      } else {
        setAvailablePlans([]);
        message.warning(response?.message || "Failed to fetch available plans");
      }
    } catch (e) {
      setAvailablePlans([]);
      message.error("Error while loading available plans");
    }
    setPlansLoading(false);
  };

  // Fetch payment history (invoices) from API
  const getPaymentHistory = async (page = 1, perPage = 20) => {
    setInvoiceLoading(true);
    try {
      const response = await MakeApiCall(
        `billing/payment-history?page=${page}&per_page=${perPage}`,
        "get",
        null,
        true,
        {},
        userData?.token
      );
      if (response?.status && response?.data?.transactions) {
        setInvoiceList(response.data.transactions);
        setInvoiceTotal(Number(response.data.pagination?.total) || 0);
      } else {
        setInvoiceList([]);
        setInvoiceTotal(0);
        message.warning(response?.message || "Failed to fetch payment history");
      }
    } catch (e) {
      setInvoiceList([]);
      setInvoiceTotal(0);
      message.error("Error while loading invoices");
    }
    setInvoiceLoading(false);
  };

  // Fetch payment methods from API (List payment methods)
  const getPaymentMethods = async () => {
    setPaymentMethodsLoading(true);
    try {
      const response = await MakeApiCall(
        `billing/payment-methods`,
        "get",
        null,
        true,
        {},
        userData?.token
      );
      if (response?.status) {
        setPaymentMethods(response.data || []);
      } else {
        setPaymentMethods([]);
        message.warning(response?.message || "Failed to fetch payment methods");
      }
    } catch (e) {
      setPaymentMethods([]);
      message.error("Error while loading payment methods");
    }
    setPaymentMethodsLoading(false);
  };

  const validateCoupon = async (couponCode, planId, billingCycle) => {
    return MakeApiCall(
      "billing/validate-coupon",
      "post",
      {
        coupon_code: couponCode.trim(),
        plan_id: planId,
        billing_cycle: billingCycle,
      },
      true,
      {},
      userData?.token
    );
  };

  // Create Razorpay order for plan subscription (Choose plan - step 1)
  const createRazorpayOrder = async (planId, billingCycle, isPlanChange = false, coupon = "") => {
    try {
      const endpoint = isPlanChange ? 'billing/change-plan' : 'billing/choose-plan';
      const payload = {
        plan_id: planId,
        billing_cycle: billingCycle,
      };
      if (coupon.trim() && ENABLE_BILLING_COUPON) {
        payload.coupon_code = coupon.trim();
      }
      const response = await MakeApiCall(
        endpoint,
        "post",
        payload,
        true,
        {},
        userData?.token
      );

      if (response?.status && response?.data) {
        return response.data; // Should contain order_id, amount, currency, etc.
      } else {
        throw new Error(response?.message || "Failed to create order");
      }
    } catch (error) {
      message.error(error.message);
      return null;
    }
  };

  // Verify payment and activate/change subscription (Verify payment - step 2)
  const verifyPaymentAndActivate = async (paymentData, planId, billingCycle, isPlanChange = false, razorpay_order_id) => {
    console.log(paymentData, razorpay_order_id);

    try {
      const endpoint = isPlanChange ? 'billing/verify-plan-change' : 'billing/verify-payment';
      // Prepare two different payloads depending on whether it's a plan change or normal payment verification
      let payload;
      if (isPlanChange) {
        payload = {
          razorpay_payment_id: paymentData.razorpay_payment_id,
          razorpay_subscription_id: paymentData.razorpay_subscription_id,
          razorpay_signature: paymentData.razorpay_signature,
          // plan_id: planId,
          // billing_cycle: billingCycle,
          // razorpay_order_id: paymentData.razorpay_order_id,
          razorpay_order_id: razorpay_order_id,
        };
      } else {
        payload = {
          razorpay_payment_id: paymentData.razorpay_payment_id,
          razorpay_subscription_id: paymentData.razorpay_subscription_id,
          razorpay_signature: paymentData.razorpay_signature,
          // razorpay_order_id: razorpay_order_id,
        };
      }

      const response = await MakeApiCall(
        endpoint,
        "post",
        payload,
        true,
        {},
        userData?.token
      );

      if (response?.status) {
        message.success(isPlanChange ? "Plan changed successfully!" : "Payment successful! Your subscription is activated.");
        // Refresh all billing data
        await getCurrentPlan();
        await getPaymentHistory();
        await getPaymentMethods();
        await getWhatsappQuota();
        return true;
      } else {
        throw new Error(response?.message || (isPlanChange ? "Plan change verification failed" : "Payment verification failed"));
      }
    } catch (error) {
      message.error(error.message);
      return false;
    }
  };

  // Common payment flow function
  const initiatePaymentFlow = async (plan, isPlanChange, billingCycle = billingPeriod) => {
    if (!isRazorpayReady) {
      message.error("Payment gateway not loaded. Please refresh the page.");
      return;
    }

    setPaymentProcessing(true);
    setGatewayOpening(true);
    setSelectedPlan(plan);

    try {
      const planCoupon = ENABLE_BILLING_COUPON ? (appliedPlanCoupons[plan.id] || "") : "";

      if (planCoupon) {
        const validation = await validateCoupon(planCoupon, plan.id, billingCycle);
        if (!validation?.status) {
          message.error(validation?.message || "Coupon is not valid for this plan");
          setPaymentProcessing(false);
          setGatewayOpening(false);
          setSelectedPlan(null);
          return;
        }
      }

      // Step 1: Create Razorpay order using appropriate API
      const orderData = await createRazorpayOrder(plan.id, billingCycle, isPlanChange, planCoupon);
      
      console.log(orderData);

      if (!orderData) {
        setPaymentProcessing(false);
        setGatewayOpening(false);
        return;
      }

      // Step 2: Initialize Razorpay checkout
      const options = {
        key: orderData.razorpay_key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: "Sellerview",
        description: isPlanChange 
          ? `Plan Change: ${currentPlan?.name} → ${plan.name} (${billingCycle === "monthly" ? "Monthly" : "Yearly"})`
          : `${plan.name} - ${billingCycle === "monthly" ? "Monthly" : "Yearly"} Plan`,
        image: "https://your-logo-url.com/logo.png",
        subscription_id: orderData.razorpay_subscription_id,

        handler: async (response) => {
          const verified = await verifyPaymentAndActivate(response, plan.id, billingCycle, isPlanChange, orderData.razorpay_order_id);
          if (verified) {
            await getAvailablePlans();
          }
          setPaymentProcessing(false);
          setSelectedPlan(null);
        },
        prefill: {
          name: ud.name || "",
          email: billingInfo.billing_email,
          contact: ud.contact_no || "",
        },
        notes: {
          plan_id: plan.id,
          plan_name: plan.name,
          billing_period: billingCycle,
          action: isPlanChange ? "plan_change" : "new_subscription",
          ...(isPlanChange && {
            old_plan_id: currentPlan?.id,
            old_plan_name: currentPlan?.name
          })
        },
        theme: {
          color: "#f45b68",
        },
        modal: {
          ondismiss: () => {
            message.info(isPlanChange ? "Plan change cancelled" : "Payment cancelled");
            setPaymentProcessing(false);
            setSelectedPlan(null);
          },
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on("payment.failed", (response) => {
        message.error(`Payment failed: ${response.error.description}`);
        setPaymentProcessing(false);
        setSelectedPlan(null);
      });
      
      razorpay.open();
      // Hide the page loader once Razorpay modal is open
      setGatewayOpening(false);
    } catch (error) {
      console.error("Subscription error:", error);
      message.error(isPlanChange ? "Failed to initiate plan change" : "Failed to initiate payment");
      setPaymentProcessing(false);
      setGatewayOpening(false);
      setSelectedPlan(null);
    }
  };

  // Helper: resolve button label for a plan card
  const getPlanActionLabel = (plan) => {
    if (!isActive || !currentPlan) return plan.cta;
    const isSamePlan = currentPlan.name === plan.name;
    if (isSamePlan) {
      if (billingPeriod === 'yearly'   && currentPlan.billing_cycle === 'monthly') return 'Switch to Annual';
      if (billingPeriod === 'monthly'  && currentPlan.billing_cycle === 'yearly')  return 'Switch to Monthly';
    }
    const currentIdx = availablePlans.findIndex((p) => p.name === currentPlan.name);
    const targetIdx  = availablePlans.findIndex((p) => p.name === plan.name);
    if (targetIdx > currentIdx) return 'Upgrade';
    if (targetIdx < currentIdx) return 'Downgrade';
    return plan.cta;
  };

  // Handle plan subscription/change with Razorpay
  const handleSubscribeToPlan = async (plan) => {
    const hasActiveSubscription = isActive && currentPlan;
    const isPlanChange = hasActiveSubscription;

    // ── Monthly billing: always show the 3-option upsell modal ──────────────
    if (billingPeriod === 'monthly') {
      const actionLabel = getPlanActionLabel(plan);
      const currentIdx = availablePlans.findIndex((p) => p.name === currentPlan?.name);
      const targetIdx  = availablePlans.findIndex((p) => p.name === plan.name);
      const isUpgrade  = targetIdx > currentIdx;
      setMonthlyConfirmModal({ open: true, plan, isPlanChange, actionLabel, isUpgrade });
      return;
    }

    // ── Yearly: upgrade / downgrade / switch to annual ─────────────────────
    if (isPlanChange) {
      const actionLabel = getPlanActionLabel(plan);
      const currentIdx = availablePlans.findIndex((p) => p.name === currentPlan.name);
      const targetIdx  = availablePlans.findIndex((p) => p.name === plan.name);
      const isUpgrade  = targetIdx > currentIdx ||
        (currentPlan.billing_cycle === 'monthly' && billingPeriod === 'yearly');

      Modal.confirm({
        className: "billing-upgrade-confirm-modal",
        title: <span className="billing-modal-title-text">{actionLabel} Plan</span>,
        content: (
          <span className="billing-modal-body-text">
            Are you sure you want to <b>{actionLabel.toLowerCase()}</b> from{' '}
            <b>{currentPlan.name}</b> to <b>{plan.name}</b>?{' '}
            {isUpgrade
              ? 'You will be charged the prorated amount for the remaining period.'
              : 'Your plan will be updated at the next billing cycle.'
            }
          </span>
        ),
        okText: <span className="billing-modal-ok-text">Proceed to Payment</span>,
        okButtonProps: { danger: true },
        cancelText: <span className="billing-modal-cancel-text">Cancel</span>,
        onOk: async () => {
          await initiatePaymentFlow(plan, true);
        },
      });
      return;
    }

    // ── New yearly subscription ────────────────────────────────────────────
    await initiatePaymentFlow(plan, false);
  };

  // Create order for saving card (Create order - save card step 1)
  const createOrderForSaveCard = async () => {
    try {
      const response = await MakeApiCall(
        `billing/payment-methods/create-order`,
        "post",
        {},
        true,
        {},
        userData?.token
      );

      if (response?.status && response?.data) {
        return response.data;
      } else {
        throw new Error(response?.message || "Failed to create order");
      }
    } catch (error) {
      message.error(error.message);
      return null;
    }
  };

  // Save token after checkout (Save token - step 2)
  const savePaymentToken = async (paymentData, cardDetails) => {
    console.log(paymentData, cardDetails);

    try {
      const body = {
        razorpay_payment_id: paymentData.razorpay_payment_id,
        razorpay_order_id: paymentData.razorpay_order_id,
        razorpay_signature: paymentData.razorpay_signature,
        card_name: cardDetails.card_name,
        set_as_default: true,
        customer_id: paymentData.razorpay_customer_id || paymentData.customer_id,
      };
      const response = await MakeApiCall(
        `billing/payment-methods/save-token`,
        "post",
        body,
        true,
        {},
        userData?.token
      );

      if (response?.status) {
        message.success("Payment method added successfully");
        await getPaymentMethods(); // Refresh payment methods
        return true;
      } else {
        throw new Error(response?.message || "Failed to save payment method");
      }
    } catch (error) {
      message.error(error.message);
      return false;
    }
  };

  // Add new payment method
  const addPaymentMethod = async (values) => {
    if (!isRazorpayReady) {
      message.error("Payment gateway not loaded");
      return;
    }

    setPaymentProcessing(true);

    try {
      // Step 1: Create order for saving card
      const orderData = await createOrderForSaveCard();
      
      console.log(orderData); 

      if (!orderData) {
        setPaymentProcessing(false);
        return;
      }

      // Step 2: Initialize Razorpay checkout for card saving
      const options = {
        key: orderData.key_id,
        amount: orderData.amount, // Usually 0 or small amount for verification
        currency: orderData.currency,
        name: "Sellerview",
        description: "Add payment method",
        image: "https://your-logo-url.com/logo.png",
        order_id: orderData.order_id,
        customer_id: orderData.customer_id, 

        handler: async (response) => {
          // Step 3: Save payment token
          // Pass along razorpay_customer_id if present in response
          response.razorpay_customer_id = response.razorpay_customer_id || orderData.customer_id;
          console.log(response);
          
          const saved = await savePaymentToken(response, values);
          if (saved) {
            setShowAddPaymentModal(false);
            addPaymentForm.resetFields();
          }
          setPaymentProcessing(false);
        },
        prefill: {
          name: values.card_name,
          email: billingInfo.billing_email,
        },
        theme: {
          color: "#f45b68",
        },
        modal: {
          ondismiss: () => {
            message.info("Payment method addition cancelled");
            setPaymentProcessing(false);
          },
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on("payment.failed", (response) => {
        message.error(`Failed to add payment method: ${response.error.description}`);
        setPaymentProcessing(false);
      });
      
      razorpay.open();
    } catch (error) {
      console.error("Add payment method error:", error);
      message.error("Failed to add payment method");
      setPaymentProcessing(false);
    }
  };

  // Set default payment method
  const setDefaultPaymentMethod = async (methodId) => {
    try {
      const response = await MakeApiCall(
        `billing/payment-methods/${methodId}/set-default`,
        "post",
        null,
        true,
        {},
        userData?.token
      );
      
      if (response?.status) {
        message.success("Default payment method updated successfully");
        getPaymentMethods();
      } else {
        message.warning(response?.message || "Failed to update default payment method");
      }
    } catch (e) {
      message.error("Error while updating default payment method");
    }
  };

  // Remove payment method
  const removePaymentMethod = async (methodId) => {
    Modal.confirm({
      className: "billing-remove-method-modal",
      title: <span className="billing-modal-title-text">Remove Payment Method</span>,
      content: (
        <span className="billing-modal-body-text">
          Remove payment method? This can't be undone.
        </span>
      ),
      okText: <span className="billing-modal-ok-text">Yes, Remove</span>,
      okButtonProps: {
        style: { background: primaryColor, borderColor: primaryColor, color: "#fff" },
      },
      centered: true,
      cancelText: <span className="billing-modal-cancel-text">No, Keep It</span>,
      onOk: async () => {
        try {
          const response = await MakeApiCall(
            `billing/payment-methods/${methodId}`,
            "delete",
            null,
            true,
            {},
            userData?.token
          );
          
          if (response?.status) {
            message.success("Payment method removed successfully");
            getPaymentMethods();
          } else {
            message.warning(response?.message || "Failed to remove payment method");
          }
        } catch (e) {
          message.error("Error while removing payment method");
        }
      },
    });
  };

  // Cancel subscription
  const cancelSubscription = () => {
    setCancelPrimaryReason(null);
    setCancelUnmetExpectations('');
    setCancelSurveyModal(true);
  };

  const submitCancelSubscription = async () => {
    if (!cancelPrimaryReason) {
      message.warning("Please select a primary reason for cancellation.");
      return;
    }
    setCancelLoading(true);
    try {
      const response = await MakeApiCall(
        `billing/cancel-subscription`,
        "post",
        {
          primary_reason: cancelPrimaryReason,
          unmet_expectations: cancelUnmetExpectations || "",
          cancel_at_cycle_end: true,
        },
        true,
        {},
        userData?.token
      );

      if (response?.status) {
        message.success("Subscription cancelled successfully");
        setCancelSurveyModal(false);
        getCurrentPlan();
      } else {
        message.warning(response?.message || "Failed to cancel subscription");
      }
    } catch (e) {
      message.error("Error while cancelling subscription");
    }
    setCancelLoading(false);
  };

  // Replaces previous renewalDate logic
  const renewalDate = useMemo(() => {
    if (billingData?.subscription?.next_due_on) {
      return dayjs.unix(billingData.subscription.next_due_on);
    }
    return null;
  }, [billingData]);

  // Set currentPlan dynamically
  const currentPlan = useMemo(() => {
    if (billingData?.subscription?.plan) {
      return {
        id: billingData.subscription.plan.id,
        name: billingData.subscription.plan.name,
        description: billingData.subscription.plan.description,
        perks: billingData.subscription.plan.features,
        billing_cycle: billingData.subscription.billing_cycle,
        billing_cycle_label: billingData.subscription.billing_cycle_label,
        status: billingData.subscription.status,
        status_label: billingData.subscription.status_label,
        is_trialing: billingData.subscription.is_trialing,
        remaining_days: billingData.subscription.remaining_days,
      };
    }
    return null;
  }, [billingData]);

  // Invoice Table Columns
  const invoiceColumns = useMemo(
    () => [
      {
        title: "Date",
        dataIndex: "date",
        key: "date",
        render: (d) => d ? dayjs(d).format("DD-MM-YYYY") : "-",
      },
      {
        title: "Plan",
        dataIndex: "plan_name",
        key: "plan_name",
        render: (p) =>
          p ? (
            <Tag className="billing-tag" >{p}</Tag>
          ) : (
            <span>-</span>
          ),
      },
      {
        title: "Amount",
        dataIndex: "formatted_amount",
        key: "formatted_amount",
        render: (a, row) => {
          if (a === undefined || a === null) return "-";
          let currency = row?.currency || "INR";
          // a is already formatted (e.g., "1,799.00"), just display with currency
          // const formattedAmount = AddCommas(a);
          return <span className="billing-amount">{currency} {a}</span>;
        },
      },
      {
        title: "Payment Method",
        key: "payment_method_label",
        dataIndex: "payment_method_label",
        render: (method) => method ? (
          <Tag color="blue" className="fw-bold billing-method-tag">{method}</Tag>
        ) : <span>-</span>,
      },
      {
        title: "Status",
        key: "status",
        dataIndex: "status",
        render: (status) =>
          status ? (
            <Tag color={status === "paid" ? "green" : status === "pending" ? "orange" : "default"}>
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </Tag>
          ) : (
            "-"
          ),
      },
      {
        title: "Invoice",
        key: "invoice",
        render: (_, row) => {
          const invoiceUrl =
            row.invoice_url || row.download_url || row.pdf_url || row.url || null;
          return invoiceUrl ? (
            <a
              href={invoiceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="billing-link"
              download
            >
              <Button type="link" icon={<DownloadOutlined />} className="billing-link">
            Download
          </Button>
            </a>
          ) : (
            <span className="billing-link billing-link-disabled">N/A</span>
          );
        },
      },
    ],
    []
  );

  const handlePlanCouponInput = (planId, value) => {
    setPlanCouponInputs((prev) => ({ ...prev, [planId]: value }));
  };

  const handleApplyPlanCoupon = async (plan) => {
    const code = (planCouponInputs[plan.id] || "").trim();
    if (!code) {
      message.warning("Please enter a promo code");
      return;
    }

    setValidatingPlanCouponId(plan.id);
    try {
      const response = await validateCoupon(code, plan.id, billingPeriod);
      if (response?.status) {
        setAppliedPlanCoupons((prev) => ({ ...prev, [plan.id]: code }));
        message.success(response?.message || "Promo code applied");
      } else {
        message.error(response?.message || "Invalid promo code");
      }
    } catch {
      message.error("Failed to validate promo code");
    }
    setValidatingPlanCouponId(null);
  };

  const handleRemovePlanCoupon = (planId) => {
    setAppliedPlanCoupons((prev) => {
      const next = { ...prev };
      delete next[planId];
      return next;
    });
    setPlanCouponInputs((prev) => {
      const next = { ...prev };
      delete next[planId];
      return next;
    });
  };

  const renderPlanCoupon = (plan, { isDisabled, isEnterprise, isCurrentPlan }) => {
    if (!ENABLE_BILLING_COUPON || isEnterprise || isDisabled || isCurrentPlan) {
      return null;
    }

    const appliedCode = appliedPlanCoupons[plan.id];
    const isValidating = validatingPlanCouponId === plan.id;

    if (appliedCode) {
      return (
        <div className="billing-plan__coupon billing-plan__coupon--applied">
          <div className="billing-plan__coupon-applied">
            <CheckCircleFilled className="billing-plan__coupon-applied-icon" />
            <span className="billing-plan__coupon-applied-code">{appliedCode}</span>
            <span className="billing-plan__coupon-applied-label">applied</span>
          </div>
          <button
            type="button"
            className="billing-plan__coupon-remove"
            onClick={() => handleRemovePlanCoupon(plan.id)}
            disabled={paymentProcessing}
          >
            Remove
          </button>
        </div>
      );
    }

    return (
      <div className="billing-plan__coupon">
        <Input
          className="billing-plan__coupon-input"
          size="small"
          placeholder="Promo code"
          value={planCouponInputs[plan.id] || ""}
          onChange={(e) => handlePlanCouponInput(plan.id, e.target.value.toUpperCase())}
          onPressEnter={() => handleApplyPlanCoupon(plan)}
          disabled={paymentProcessing || isValidating}
          allowClear
        />
        <Button
          className="billing-plan__coupon-apply"
          size="small"
          type="primary"
          danger
          onClick={() => handleApplyPlanCoupon(plan)}
          loading={isValidating}
          disabled={
            !(planCouponInputs[plan.id] || "").trim() ||
            paymentProcessing ||
            isValidating
          }
        >
          Apply
        </Button>
      </div>
    );
  };

  const scrollPlans = (direction) => {
    const el = plansScrollRef.current;
    if (!el) return;
    const delta = direction === "left" ? -420 : 420;
    el.scrollBy({ left: delta, behavior: "smooth" });
  };

  const getPlanPriceString = (p) => {
    if (!p) return "";
    // For yearly billing, show the per-month equivalent (formattedPriceYearly)
    // The unit is always "/ month"; "billed annually" is shown as a subtext below
    if (billingPeriod === "monthly") {
      return `${p.formattedPriceMonthly} ${p.currency}`;
    }
    return `${p.formattedPriceYearly} ${p.currency}`;
  };

  const getCurrentPlan = async () => {
    setLoading(true);
    const response = await MakeApiCall(`billing/my-subscription`, "get", null, true, {}, userData?.token);

    if (response?.status && response?.data) {
      // console.log(response);
      setBillingData(response.data);
    } else {
      message.warning(response?.message || "Failed to fetch billing info");
      setBillingData(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (userData?.token) {
      getCurrentPlan();
      getAvailablePlans();
      getPaymentHistory(invoicePage, invoicePerPage);
      getPaymentMethods();
    }
  }, [userData?.token]);

  useEffect(() => {
    if (!userData?.token || hasSyncedBillingVisitRef.current) return;

    hasSyncedBillingVisitRef.current = true;
    syncProperties({
      sv_visited_billing_page: formatToHubSpotDateTime(),
      sv_pricing_page_visit_count: 1,
    });
  }, [userData?.token, syncProperties]);

  useEffect(() => {
    if (userData?.token) {
      getPaymentHistory(invoicePage, invoicePerPage);
    }
    // eslint-disable-next-line
  }, [invoicePage, invoicePerPage]);

  useEffect(() => {
    if (!ENABLE_BILLING_COUPON) return;
    setAppliedPlanCoupons({});
    setPlanCouponInputs({});
  }, [billingPeriod]);

  const isActive = !!billingData?.has_subscription && !!billingData?.subscription?.is_active;
  // Shortcut to the actual user data object (lives at userData.user.user_data)
  const ud = userData?.user?.user_data || {};
  const isTrialUser = !isActive && !!ud.has_trial && !ud.is_trial_expired;

  // Calculate WhatsApp usage percentage
  const whatsappUsagePercentage = whatsappQuota && whatsappQuota.whatsapp_quota_limit > 0
    ? (whatsappQuota.whatsapp_sends_used / whatsappQuota.whatsapp_quota_limit) * 100
    : 0;

  // Get progress bar color based on usage
  const getProgressColor = () => {
    if (whatsappUsagePercentage >= 90) return "#ff4d4f";
    if (whatsappUsagePercentage >= 70) return "#faad14";
    return "#52c41a";
  };

  return (
    <Wrapper>
      <Helmet>
        <title>Billing</title>
      </Helmet>

      <div className="billing-header ">
        <div className="billing-header__left">
          <div className="billing-title">Billing</div>
          <div className="billing-subtitle">Manage your plan, download invoices, and update payment methods.</div>
        </div>
        <div className="billing-header__right">
        <div className="billing-status ">
          <span className="billing-status__icon">
              <i
              className={`bi billing-status-icon ${billingData?.subscription?.is_trialing || isTrialUser ? "bi-hourglass-split" : isActive ? "bi-patch-check-fill" : "bi-x-circle-fill"}`}
              data-active={billingData?.subscription?.is_trialing || isTrialUser || isActive}
            />
          </span>
          
            <span className="billing-status__text">
            {billingData?.subscription?.cancel_at_period_end
              ? billingData.subscription.status_label
              : (
                <>
                  {billingData?.subscription?.is_trialing
                    ? "Trialing"
                    : isTrialUser
                    ? `On Trial · ${ud.remaining_trial_days ?? 0} days left`
                    : isActive ? "Active" : "Inactive"}
                  {renewalDate && (<span className="billing-status__dot">•</span>)}
                  {renewalDate && (
                    <>
                      {" "}
                      {billingData?.subscription?.next_due_on_label || "Next renewal:"}{" "}
              <b>{renewalDate.format("MMM D, YYYY")}</b>
                    </>
                  )}
                </>
              )
            }
            </span>
          </div>
        </div>
      </div>

     

      {/* Plans */}
      <div className="row gy-5 g-xl-5">
        <div className="col-xxl-12">
          <div className="card card-xxl-stretch mb-5 mb-xl-8 billing-card">
            <div className="card-header border-bottom-dashed billing-card__header">
              <div>
                <div className="billing-section-title">Plans</div>
                <div className="billing-section-subtitle">Upgrade or downgrade anytime.</div>
              </div>
              <div className="billing-period">
                <div className="billing-period__label">Billing:</div>
                <div className="billing-period__toggle">
                  <button
                    className={`billing-pill ${billingPeriod === "monthly" ? "is-active" : ""}`}
                    onClick={() => setBillingPeriod("monthly")}
                    type="button"
                  >
                    Monthly
                  </button>
                  <button
                    className={`billing-pill ${billingPeriod === "yearly" ? "is-active" : ""}`}
                    onClick={() => setBillingPeriod("yearly")}
                    type="button"
                  >
                    Yearly
                  </button>
                </div>
              </div>
            </div>

            <div className="card-body pt-6">
              <div className="billing-plans-grid row gy-5">
                <div className="col-lg-3">
                  <div className="billing-current ">
                    <div className="billing-current__badge">
                      {currentPlan?.is_trialing
                        ? "TRIALING"
                        : isTrialUser
                        ? "ON TRIAL"
                        : isActive
                        ? "CURRENT PLAN"
                        : "No Active Plan"}
                    </div>
                    <div className="billing-current__name">
                      {currentPlan ? currentPlan.name : isTrialUser ? "Trial" : "N/A"}
                    </div>
                  <div className="billing-current__price">
                      {currentPlan ? (
                        <>
                          {currentPlan.billing_cycle_label && (
                            <span>
                              {currentPlan.billing_cycle_label}
                              {currentPlan.remaining_days !== undefined &&
                                currentPlan.is_trialing && (
                                  <span>
                                    {" "}
                                    · {currentPlan.remaining_days} days left
                                  </span>
                                )}
                            </span>
                          )}
                        </>
                      ) : isTrialUser ? (
                        <span>{ud.remaining_trial_days ?? 0} days left</span>
                      ) : null}
                  </div>
                    <div className="billing-current__desc">
                      {currentPlan ? currentPlan.description : ""}
                  </div>

                    {currentPlan?.perks && (
                      <ul className="billing-plan__perks">
                        {currentPlan.perks.map((perk) => (
                          <li key={perk}>
                            <span className="billing-check" />
                            {perk}
                          </li>
                        ))}
                      </ul>
                    )}
                    {isActive && (
                      <Button 
                        danger 
                        onClick={cancelSubscription}
                        className="h-35px"
                        disabled={!!billingData?.subscription?.cancel_at_period_end}
                        title={billingData?.subscription?.cancel_at_period_end ? "Cancellation already scheduled at end of billing period" : undefined}
                      >
                        {billingData?.subscription?.cancel_at_period_end
                          ? "Cancellation Scheduled"
                          : "Cancel Subscription"
                        }
                      </Button>
                    )}
                    <div className="billing-hint mt-4">
                    You can switch plans anytime. If you downgrade, features update based on your new plan.
                  </div>
                </div>
                </div>
                <div className="col-lg-9">
                <div className="billing-available">
                  <div className="billing-available__top">
                      <div className="billing-available__title">Available Plans</div>
                    <div className="billing-available__nav">
                      <Button
                        className="billing-icon-btn"
                        icon={<LeftOutlined />}
                        onClick={() => scrollPlans("left")}
                          disabled={plansLoading}
                      />
                      <Button
                        className="billing-icon-btn"
                        icon={<RightOutlined />}
                        onClick={() => scrollPlans("right")}
                          disabled={plansLoading}
                      />
                    </div>
                  </div>

                    <div className="billing-scroll mt-5 pb-5" ref={plansScrollRef}>
                      {plansLoading ? (
                        <div className="p-4">Loading plans...</div>
                      ) : availablePlans.length === 0 ? (
                        <div className="p-4">No plans found.</div>
                      ) : (
                       availablePlans.map((p) => {
                        // Compare both plan 'name' and 'billing_cycle' to determine current plan
                        const isCurrentPlan = currentPlan?.name === p.name && currentPlan?.billing_cycle === billingPeriod;
                        const isDisabled = !p.enabled;
                        const isOrderCountExceeded = lastMonthOrderCount !== null && p.maxOrderCount !== undefined && lastMonthOrderCount > p.maxOrderCount;

                        const disabledReason = isDisabled
                          ? isOrderCountExceeded
                            ? `Your recent order volume (${lastMonthOrderCount?.toLocaleString()} orders in last 30 days) exceeds this plan's limit of ${p.maxOrderCount?.toLocaleString()} orders/month. Please choose a higher plan.`
                            : "This plan is not currently available for your account."
                          : null;

                        const isEnterprise = p.name?.toLowerCase().includes("enterprise");

                        return (
                          <div
                            key={p.key}
                            className={`billing-plan ${p.highlighted ? "is-highlighted" : ""}`}
                          >
                            {/* Dark overlay for disabled/unavailable plans */}
                            {isDisabled && (
                              <div className="billing-plan__disabled-overlay">
                                <div className="billing-plan__disabled-overlay-content">
                                  <span className="billing-plan__disabled-overlay-icon">
                                    <i className="bi bi-slash-circle" />
                                  </span>
                                  <div className="billing-plan__disabled-overlay-title">
                                    {isOrderCountExceeded ? "Order limit exceeded" : "Plan unavailable"}
                                  </div>
                                  <div className="billing-plan__disabled-overlay-text">
                                    {disabledReason}
                                  </div>
                                </div>
                              </div>
                            )}

                            <div className="billing-plan__name mb-4 d-flex align-items-center gap-2">
                              {p.name}
                              {billingPeriod === "yearly" && !isDisabled && !isEnterprise && (
                                <span className="billing-save-badge">
                                  Save 20%
                                </span>
                              )}
                            </div>

                            {/* Price block — Enterprise shows "Custom Pricing" */}
                            {isEnterprise ? (
                              <div className="billing-plan__price">
                                <span className="billing-plan__custom-pricing">Custom Pricing</span>
                              </div>
                            ) : (
                              <div className="billing-plan__price">
                                <span className="billing-plan__priceValue">
                                  {getPlanPriceString(p)}
                                </span>
                                <span className="billing-plan__priceUnit">
                                  / month
                                </span>
                                {billingPeriod === "yearly" && (
                                  <span className="billing-billed-annually">
                                    billed annually
                                  </span>
                                )}
                              </div>
                            )}

                            <div className="billing-current__desc">
                              {p ? p.description : ""}
                            </div>
                            {p.maxOrderCount !== undefined && (
                              <div className={`billing-plan__order-limit ${isOrderCountExceeded ? "is-exceeded" : ""}`}>
                                <span className="billing-plan__order-limit-icon">
                                  <i className="bi bi-box-seam" />
                                </span>
                                <div>
                                  <div className="billing-plan__order-limit-main">
                                    Up to {p.maxOrderCount.toLocaleString()} orders/month
                                  </div>
                                  {lastMonthOrderCount !== null && (
                                    <div className="billing-plan__order-limit-sub">
                                      {lastMonthOrderCount.toLocaleString()} orders in last 30 days
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                            <ul className="billing-plan__perks">
                              {p.perks.map((perk) => (
                                <li key={perk}>
                                  <span className="billing-check" />
                                  {perk}
                                </li>
                              ))}
                            </ul>

                            {renderPlanCoupon(p, { isDisabled, isEnterprise, isCurrentPlan })}

                            {/* CTA — Enterprise: Talk to Sales → Calendly; others: normal subscribe */}
                            {isEnterprise ? (
                              <Button
                                className="billing-plan__cta billing-plan__cta--enterprise"
                                type="default"
                                onClick={() => {
                                  setBookCallLoading(true);
                                  setBookCallOpen(true);
                                }}
                              >
                                Talk to Sales
                              </Button>
                            ) : (
                              <Button
                                className="billing-plan__cta"
                                type={p.highlighted ? "primary" : "default"}
                                danger
                                onClick={() => handleSubscribeToPlan(p)}
                                loading={paymentProcessing && selectedPlan === p}
                                disabled={
                                  paymentProcessing ||
                                  !isRazorpayReady ||
                                  isCurrentPlan ||
                                  isDisabled
                                }
                              >
                                {isCurrentPlan ? "Current Plan" : getPlanActionLabel(p)}
                              </Button>
                            )}
                          </div>
                        );
                      })
                      )}
                  </div>

                  <div className="billing-scrollbar-hint" />
                </div>
                </div>

                <div className="col-lg-12">
                   {/* WhatsApp Quota Card */}
                {(whatsappQuota?.has_active_subscription || isActive) && (
                  <div className="row gy-5 g-xl-5 billing-whatsapp-row">
                    <div className="col-xxl-12">
                      <div className="card billing-card">
                        <div className="card-body pb-3 pt-5">
                          <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-3">
                            <div className="d-flex align-items-center gap-3">
                              <div>
                                <WhatsAppOutlined className="billing-whatsapp-icon" />
                              </div>
                              <div>
                                <div className="billing-section-title billing-whatsapp-title">
                                Whatsapp Alerts Usage Limits
                                </div>
                                
                              </div>
                              {whatsappQuota?.is_shared_across_child_sellers && (
                                    <Tag color="blue" className="billing-small-tag">
                                      Shared Across All Seller Accounts
                                    </Tag>
                                  )}
                            </div>
                            {whatsappQuota?.is_unlimited && (
                              <Tag color="green" className="billing-unlimited-tag">
                                Unlimited Messages
                              </Tag>
                            )}
                            
                          </div>

                          <Spin spinning={whatsappQuotaLoading}>
                            {whatsappQuota && (
                              <div className="">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <div className=" min-w-175px d-flex align-items-center">
                                    <span className="billing-whatsapp-usage-value">
                                      {whatsappQuota.whatsapp_sends_used?.toLocaleString()}
                                    </span>
                                    <span className="billing-whatsapp-usage-max">
                                      / {whatsappQuota.is_unlimited ? "Unlimited" : whatsappQuota.whatsapp_quota_limit?.toLocaleString()} messages
                                    </span>
                                  </div>
                                  <Progress
                                  percent={whatsappUsagePercentage}
                                  showInfo={false}
                                  strokeColor={getProgressColor()}
                                  trailColor="var(--app-border)"
                                  strokeWidth={10}
                                  style={{ marginBottom: 0, minWidth: 160 }}
                                />
                                  <div className="min-w-90px text-end">
                                    <span className="billing-whatsapp-usage-pct" style={{ color: getProgressColor() }}>
                                      {whatsappUsagePercentage.toFixed(1)}% used
                                    </span>
                                  </div>
                                </div>
                               
                                {/* <div className="d-flex justify-content-between align-items-center mt-2">
                                  <div style={{ fontSize: 12, color: "#888" }}>
                                    {whatsappQuota.whatsapp_quota_remaining?.toLocaleString()} messages remaining
                                  </div>
                                  {whatsappQuota?.is_shared_across_child_sellers && (
                                    <Tag color="blue" style={{ fontSize: 11 }}>
                                      Shared Across All Sellers
                                    </Tag>
                                  )}
                                </div> */}
                              </div>
                            )}
                          </Spin>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Invoices */}
      <div className="row gy-5 g-xl-5">
        <div className="col-xxl-12">
          <div className="card card-xxl-stretch mb-5 mb-xl-8 billing-card">
            <div className="card-header border-bottom-dashed billing-card__header">
              <div>
                <div className="billing-section-title">Invoices</div>
                <div className="billing-section-subtitle">Download previous invoices for your records.</div>
              </div>
            </div>
            <div className="card-body pt-4">
              <ConfigProvider theme={{ token: { colorPrimary: "#f45b68" } }}>
                <div className={isMobile ? "" : "table-responsive"}>
                  <Spin spinning={invoiceLoading}>
                    {isMobile ? (
                      <div>
                        {invoiceList?.length ? (
                          invoiceList.map((row, idx) => {
                            const invoiceUrl = row.invoice_url || row.download_url || row.pdf_url || row.url || null;
                            const status = row?.status || "-";
                            return (
                              <>
                              <Card key={row.id || row.transaction_id}  bodyStyle={{ paddingBottom: 10 }} className="mb-3 billing-invoice-mobile-card">
                              <div className="card-content">
                                <Row gutter={[8, 8]} className="mb-2">
                                  <Col span={12}>
                                    <div className="fs-7">
                                      <span className="text-muted">Date:</span>
                                      <span className="ms-2 fw-semibold">{row.date ? dayjs(row.date).format("DD-MM-YYYY") : "-"}</span>
                                    </div>
                                  </Col>
                                  <Col span={12}>
                                    <div className="fs-7">
                                      <span className="text-muted">Plan:</span>
                                      <span className="ms-2">{row.plan_name || "-"}</span>
                                    </div>
                                  </Col>
                                </Row>
                                <Row gutter={[8, 8]} className="mb-2">
                                  <Col span={12}>
                                    <div className="fs-7">
                                      <span className="text-muted">Amount:</span>
                                      <span className="ms-2 fw-bold text-primary">{row.currency || "INR"} {row.formatted_amount || "-"}</span>
                                    </div>
                                  </Col>
                                  <Col span={12}>
                                    <div className="fs-7">
                                      <span className="text-muted">Status:</span>
                                      <span className="ms-2">
                                        <Tag color={row.status === "paid" ? "green" : "orange"}>{row.status || "-"}</Tag>
                                      </span>
                                    </div>
                                  </Col>
                                </Row>
                                <div className="mb-2">
                                  <div className="fs-7">
                                    <span className="text-muted">Payment Method:</span>
                                    <span className="ms-2">{row.payment_method_label || "-"}</span>
                                  </div>
                                </div>
                                {(row.invoice_url || row.download_url) && (
                                  <Button type="link" className="p-0 mt-2" href={row.invoice_url || row.download_url} target="_blank" download>
                                    Download Invoice <DownloadOutlined />
                                  </Button>
                                )}
                              </div>
                            </Card>
                            </>
                            );
                          })
                        ) : (
                          <div className="text-muted py-5 text-center">{invoiceLoading ? "Loading..." : "No invoices found."}</div>
                        )}
                      </div>
                    ) : (
                  <Table
                    columns={invoiceColumns}
                        dataSource={invoiceList}
                        rowKey={(row) => row.id || row.transaction_id || row.invoice_id}
                    pagination={false}
                        locale={{
                          emptyText: invoiceLoading
                            ? "Loading..."
                            : "No invoices found."
                        }}
                    components={{
                      table: (p) => (
                        <table className="table table-row-dashed table-row-gray-300 align-middle gs-4 gy-4 fs-7" {...p} />
                      ),
                      header: { row: (p) => <tr {...p} className="fw-bolder billing-invoice-header-row bg-cus" /> },
                    }}
                      />
                    )}
                  </Spin>
                  <div className="d-flex justify-content-end mt-4">
                    <Pagination
                      current={invoicePage}
                      pageSize={invoicePerPage}
                      total={invoiceTotal}
                      onChange={(page, pageSize) => {
                        setInvoicePage(page);
                        setInvoicePerPage(pageSize);
                      }}
                      showSizeChanger
                      pageSizeOptions={["5", "10", "20", "50", "100"]}
                      size="small"
                    />
                  </div>
                </div>
              </ConfigProvider>
            </div>
          </div>
        </div>
      </div>

      {/* Payment methods */}
      <div className="row gy-5 g-xl-5">
        <div className="col-xxl-12">
          <div className="card card-xxl-stretch mb-5 mb-xl-8 billing-card">
            <div className="card-header border-bottom-dashed billing-card__header">
              <div>
                <div className="billing-section-title">Payment methods</div>
                <div className="billing-section-subtitle d-none d-lg-block">Manage cards used for workspace billing.</div>
              </div>
              <Button
                className="billing-btn px-3"
                icon={
                  !isMobile ? <PlusOutlined /> : null
                }
                onClick={() => setShowAddPaymentModal(true)}
                disabled={!isRazorpayReady}
              >
                Add payment method
              </Button>
            </div>
            <div className="card-body pt-6">
              <Spin spinning={paymentMethodsLoading}>
              <div className="billing-payments-grid">
                <div className="billing-payments">
                    {paymentMethods.length === 0 ? (
                      <div className="billing-no-payment-methods">
                        No payment methods found. Add one to get started.
                      </div>
                    ) : (
                      paymentMethods.map((pm) => (
                        <div key={pm.id} className={`mb-4 billing-payCard ${pm.is_default ? "is-default" : ""}`}>
                      <div className="billing-payCard__left">
                            <div className="billing-payCard__brand">
                              {(() => {
                                const brand = (pm.card_brand || '').toLowerCase();
                                const methodType = (pm.method_type || '').toLowerCase();
                                if (brand === 'visa') return <img src="/assets/media/svg/card-logos/visa.svg" alt="Visa" style={{ width: 40, height: 'auto' }} />;
                                if (brand === 'mastercard') return <img src="/assets/media/svg/card-logos/mastercard.svg" alt="Mastercard" style={{ width: 40, height: 'auto' }} />;
                                if (brand === 'amex' || brand === 'american express') return <img src="/assets/media/svg/card-logos/american-express.svg" alt="Amex" style={{ width: 40, height: 'auto' }} />;
                                if (methodType === 'upi') return <img src="/assets/media/svg/card-logos/UPI.svg" alt="UPI" style={{ width: 40, height: 'auto' }} />;
                                return <CreditCardOutlined className="billing-fallback-card-icon" />;
                              })()}
                            </div>
                        <div className="billing-payCard__meta">
                          <div className="billing-payCard__number">
                                {(pm.method_type || '').toLowerCase() === 'upi'
                                  ? <>{pm.upi_vpa || '9876543210@ybl'}</>
                                  : <>{pm.card_brand} ending •••• {pm.card_last4}</>
                                }
                                {pm.is_default && <span className="billing-defaultTag">Default</span>}
                          </div>
                              {(pm.method_type || '').toLowerCase() !== 'upi' && (
                                <div className="billing-payCard__name">Name on card: {pm.name_on_card || pm.name}</div>
                              )}
                              {pm.expiry_month && pm.expiry_year && (
                                <div className="billing-payCard__expiry">
                                  Expires: {pm.expiry_month}/{pm.expiry_year}
                                </div>
                              )}
                        </div>
                      </div>
                      <div className="billing-payCard__actions">
                            <Button 
                              className="billing-btn" 
                              disabled={pm.is_default}
                              onClick={() => setDefaultPaymentMethod(pm.id)}
                            >
                          Set default
                        </Button>
                            <Button 
                              className="billing-btn" 
                              danger 
                              ghost
                              onClick={() => removePaymentMethod(pm.id)}
                              disabled={pm.is_default}
                            >
                          Remove
                        </Button>
                      </div>
                    </div>
                      ))
                    )}
                </div>

                <div className="billing-billingInfo">
                  <div className="billing-billingInfo__title">Billing info</div>
                    <Spin spinning={billingInfoLoading}>
                  <div className="billing-billingInfo__row">
                    <div className="billing-billingInfo__label">Workspace:</div>
                        <div className="billing-billingInfo__value">{billingInfo.workspace || "-"}</div>
                  </div>
                  <div className="billing-billingInfo__row">
                    <div className="billing-billingInfo__label">Billing email:</div>
                        <div className="billing-billingInfo__value">{billingInfo.billing_email || "-"}</div>
                  </div>
                  <div className="billing-billingInfo__row">
                    <div className="billing-billingInfo__label">Currency:</div>
                        <div className="billing-billingInfo__value">{billingInfo.currency || "-"}</div>
                  </div>
                      <div className="billing-billingInfo__row">
                        <div className="billing-billingInfo__label">Address:</div>
                        <div className="billing-billingInfo__value">{billingInfo.address || "-"}</div>
                      </div>
                      <div className="billing-billingInfo__row">
                        <div className="billing-billingInfo__label">GST Number:</div>
                        <div className="billing-billingInfo__value">{billingInfo.gst_number || "-"}</div>
                      </div>
                      <Button
                          className="billing-btn w-100 mt-5"
                          danger
                          ghost
                          onClick={() => {
                            billingInfoForm.setFieldsValue(billingInfo);
                            setShowBillingInfoModal(true);
                          }}
                        >
                      {billingInfo?.workspace || billingInfo?.billing_email ? "Update billing info" : "Add billing info"}
                  </Button>
                  <div className="billing-hint mt-3 ">
                        Keep billing info updated to receive invoices and payment alerts.
                  </div>
                    </Spin>
              </div>
            </div>
              </Spin>
          </div>
        </div>
      </div>
      </div>

      {/* Add Payment Method Modal */}
      <Modal
        title={<span className="billing-modal-title-text">Add Payment Method</span>}
        open={showAddPaymentModal}
        className="billing-add-method-modal"
        onCancel={() => {
          setShowAddPaymentModal(false);
          addPaymentForm.resetFields();
        }}
        footer={null}
        width={500}
        centered
      >
        <Form
          form={addPaymentForm}
          layout="vertical"
          onFinish={addPaymentMethod}
        >
          <Form.Item
            label="Name on Card"
            name="card_name"
            rules={[{ required: true, message: "Please enter name on card" }]}
          >
            <Input placeholder="John Doe" />
          </Form.Item>
          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={paymentProcessing}
              block
              danger
            >
              Proceed to Add Card
            </Button>
          </Form.Item>
          <div className="billing-hint">
            You will be redirected to Razorpay secure checkout to add your card details.
          </div>
        </Form>
      </Modal>

      {/* Update Billing Info Modal */}
      <Modal
        title={
          <span className="billing-modal-title-text">
            {billingInfo?.workspace || billingInfo?.billing_email ? "Update Billing Info" : "Create Billing Info"}
          </span>
        }
        open={showBillingInfoModal}
        className="billing-info-modal"
        onCancel={() => {
          setShowBillingInfoModal(false);
          billingInfoForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form
          form={billingInfoForm}
          layout="vertical"
          initialValues={billingInfo}
          onFinish={saveBillingInfo}
        >
          <Form.Item
            label="Workspace"
            name="workspace"
            rules={[{ required: true, message: "Workspace is required" }]}
          >
            <Input placeholder="Workspace name" />
          </Form.Item>
          <Form.Item
            label="Billing email"
            name="billing_email"
            rules={[
              { required: true, message: "Billing email is required" },
              { type: "email", message: "Enter a valid email" }
            ]}
          >
            <Input placeholder="billing@email.com" />
          </Form.Item>
          <Form.Item
            label="Currency"
            name="currency"
            rules={[{ required: true, message: "Currency is required" }]}
          >
            <Input placeholder="INR" />
          </Form.Item>
          <Form.Item
            label="Billing address"
            name="address"
          >
            <Input placeholder="Optional address for invoices" />
          </Form.Item>
          <Form.Item
            label="GST Number"
            name="gst_number"
          >
            <Input placeholder="Optional GST Number" />
          </Form.Item>
          <Form.Item>
            <Button 
              type="primary"
              htmlType="submit"
              loading={billingInfoLoading}
              className="h-40px"
              block
              danger
            >
              {billingInfo?.workspace || billingInfo?.billing_email ? "Save changes" : "Create billing info"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Monthly Plan — 3-option confirmation modal */}
      <Modal
        open={monthlyConfirmModal.open}
        onCancel={() => setMonthlyConfirmModal({ open: false, plan: null, isPlanChange: false, actionLabel: '', isUpgrade: false })}
        styles={{ header: { borderBottom: 'none', paddingBottom: 0 }, footer: { borderTop: 'none' } }}
        className="billing-monthly-confirm-modal"
        title={
          <span className="billing-modal-title-wrap">
            <ExclamationCircleFilled className="billing-modal-title-icon" />
            <span className="billing-modal-title-text">
              {monthlyConfirmModal.actionLabel ? `${monthlyConfirmModal.actionLabel} Plan` : 'Confirm Plan'}
            </span>
          </span>
        }
        footer={[
          <Button
            key="annual"
            className="billing-switch-annual-btn"
            onClick={() => {
              setMonthlyConfirmModal({ open: false, plan: null, isPlanChange: false, actionLabel: '', isUpgrade: false });
              setBillingPeriod('yearly');
            }}
            disabled={paymentProcessing}
          >
            Switch to Annual
          </Button>,
          <Button
            key="proceed"
            type="primary"
            danger
            loading={paymentProcessing}
            onClick={() => {
              const { plan, isPlanChange } = monthlyConfirmModal;
              setMonthlyConfirmModal({ open: false, plan: null, isPlanChange: false, actionLabel: '', isUpgrade: false });
              initiatePaymentFlow(plan, isPlanChange, 'monthly');
            }}
          >
            Proceed to Payment
          </Button>,
        ]}
        width={440}
        centered
      >
        {monthlyConfirmModal.plan && (
          <span className="billing-modal-body-text">
            {monthlyConfirmModal.isPlanChange ? (
              <>
                Are you sure you want to <b>{monthlyConfirmModal.actionLabel.toLowerCase()}</b> from{' '}
                <b>{currentPlan?.name}</b> to <b>{monthlyConfirmModal.plan.name}</b>?{' '}
                {monthlyConfirmModal.isUpgrade
                  ? 'You will be charged the prorated amount for the remaining period.'
                  : 'Your plan will be updated at the next billing cycle.'
                }
              </>
            ) : (
              <>You're about to subscribe to <b>{monthlyConfirmModal.plan.name}</b> on a monthly basis.</>
            )}
            {' '}You can save 20% by switching to the annual plan.
          </span>
        )}
      </Modal>

      {/* Cancel Subscription Survey Modal */}
      <Modal
        title={
          <span className="billing-modal-title-wrap">
            <ExclamationCircleFilled className="billing-modal-title-icon" />
            <span className="billing-modal-title-text">
              Cancel Subscription
            </span>
          </span>
        }
        open={cancelSurveyModal}
        onCancel={() => setCancelSurveyModal(false)}
        footer={[
          <Button key="keep" onClick={() => setCancelSurveyModal(false)} disabled={cancelLoading}>
            No, Keep It
          </Button>,
          <Button
            key="cancel"
            type="primary"
            danger
            loading={cancelLoading}
            disabled={!cancelPrimaryReason}
            onClick={submitCancelSubscription}
          >
            Yes, Cancel Subscription
          </Button>,
        ]}
        width={520}
        styles={{ header: { borderBottom: '1px solid var(--app-border)', paddingBottom: 12 } }}
        className="billing-cancel-modal"
      >
        <div className="billing-cancel-modal-content">
          <div className="billing-cancel-modal-desc">
            We're sorry to see you go. Before you leave, please help us understand why.
          </div>

          {/* Input 01 — Primary reason */}
          <div className="billing-cancel-modal-section">
            <div className="billing-cancel-modal-label">
              Primary reason <span className="billing-cancel-modal-required">*</span>
            </div>
            <Radio.Group
              value={cancelPrimaryReason}
              onChange={(e) => setCancelPrimaryReason(e.target.value)}
              className="billing-cancel-modal-radio-group"
            >
              {(billingData?.cancellation_survey?.primary_reasons || []).map((reason) => (
                <Radio
                  key={reason.id}
                  value={reason.id}
                  className="billing-cancel-modal-radio"
                >
                  {reason.label}
                </Radio>
              ))}
            </Radio.Group>
          </div>

          {/* Input 02 — Unmet expectations */}
          <div>
            <div className="billing-cancel-modal-label">
              What did you expect that you didn't get?{' '}
              <span className="billing-cancel-modal-optional">(optional)</span>
            </div>
            <Input.TextArea
              rows={3}
              placeholder="Tell us what was missing or didn't meet your expectations…"
              value={cancelUnmetExpectations}
              onChange={(e) => setCancelUnmetExpectations(e.target.value)}
              maxLength={500}
              showCount
              className="billing-cancel-modal-textarea"
            />
          </div>
        </div>
      </Modal>

      {/* Book a call / Talk to Sales — Calendly modal */}
      {(() => {
        const calendlyBase = 'https://calendly.com/himanshu-adsify/sellerview-agency-demo-clone';
        const calendlyParams = new URLSearchParams();
        if (ud.name)  calendlyParams.set('name',  ud.name);
        if (ud.email) calendlyParams.set('email', ud.email);
        const qs = calendlyParams.toString();
        const calendlySrc = qs ? `${calendlyBase}?${qs}` : calendlyBase;
        return (
          <BookACallModalUI
            open={bookCallOpen}
            onClose={() => setBookCallOpen(false)}
            loading={bookCallLoading}
            onLoad={() => setBookCallLoading(false)}
            calendlySrc={calendlySrc}
          />
        );
      })()}

      {/* Page loader — shown while waiting for payment gateway to open */}
      {gatewayOpening && (
        <div className="billing-gateway-loader">
          <div className="billing-gateway-loader__content">
            <Spin size="large" />
            <div className="billing-gateway-loader__title">Opening payment gateway…</div>
            <div className="billing-gateway-loader__subtitle">Please wait, you will be redirected shortly.</div>
          </div>
        </div>
      )}
    </Wrapper>
  );
};

export default Billing;